'use client';

import { Box, Card, Center, Heading, HStack, Spinner, Stack } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import type React from 'react';

import { useI18n } from '@/__generated__/i18n.next';
import { SOLVE_ACTION_TYPE } from '@/infrastructures/motiAI/constants';
import { fetchMotiLeaderboard } from '@/infrastructures/motiAI/motiClient';
import { dayjs } from '@/utils/dayjs';
import { wakeUpMotiAI } from '@/utils/ping';

interface Props {
  userId: number;
  tags?: string[];
}

interface LeaderboardEntry {
  rank: number;
  username: string;
  medal?: string;
  isCurrentUser: boolean;
  points: number;
}

export const RankingCards: React.FC<Props> = (props) => {
  const t = useI18n();
  const actorId = String(props.userId);

  // Daily ranking (today only)
  const { data: dailyLeaderboardData = [], isLoading: isLoadingDailyLeaderboard } = useQuery({
    queryKey: ['dailyLeaderboard', actorId],
    queryFn: async () => {
      wakeUpMotiAI();
      const { since, until } = (() => {
        const now = dayjs();
        return {
          since: now.startOf('day').toISOString(),
          until: now.endOf('day').toISOString(),
        };
      })();
      return await fetchLeaderboardData(actorId, props.tags?.[0], since, until);
    },
  });

  // Monthly ranking (current month)
  const { data: monthlyLeaderboardData = [], isLoading: isLoadingMonthlyLeaderboard } = useQuery({
    queryKey: ['monthlyLeaderboard', actorId],
    queryFn: async () => {
      wakeUpMotiAI();
      const { since, until } = (() => {
        const now = dayjs();
        return {
          since: now.startOf('month').hour(0).minute(0).second(0).millisecond(0).toISOString(),
          until: now.endOf('month').toISOString(),
        };
      })();
      return await fetchLeaderboardData(actorId, props.tags?.[0], since, until);
    },
  });

  return (
    <Box minH="18rem" pos="relative">
      <RankingCard
        cardProps={{
          bgColor: 'brand.100',
          bottom: 0,
          color: 'brand.900',
          left: 0,
          pe: 14,
          position: 'absolute',
          top: 0,
          variant: 'filled',
          width: 'calc(50% + 1rem)',
        }}
        data={dailyLeaderboardData}
        isLoading={isLoadingDailyLeaderboard}
        t={t}
        title={t.components.rankingStreakCard.dailyRanking()}
      />

      <RankingCard
        cardProps={{
          borderRadius: 'md',
          bottom: 0,
          boxShadow: 'sm',
          position: 'absolute',
          right: 0,
          top: 0,
          width: 'calc(50% + 1rem)',
        }}
        data={monthlyLeaderboardData}
        isLoading={isLoadingMonthlyLeaderboard}
        t={t}
        title={t.components.rankingStreakCard.monthlyRanking()}
      />
    </Box>
  );
};

type RankingCardProps = {
  title: string;
  data: LeaderboardEntry[];
  isLoading: boolean;
  cardProps: Record<string, unknown>;
  t: ReturnType<typeof useI18n>;
} & Record<string, unknown>;

const RankingCard: React.FC<RankingCardProps> = (props) => (
  <Card.Root as={Stack} gap={2} p={6} {...props.cardProps}>
    <Heading size="md">{props.title}</Heading>

    {props.isLoading ? (
      <Center flex={1}>
        <Spinner color="brand.solid" size="lg" />
      </Center>
    ) : (
      <Stack alignSelf="stretch" gap={1}>
        {props.data.map((item, index) => (
          <HStack key={index} gap={4} {...(item.isCurrentUser ? { rounded: 'full', bgColor: 'brand.50', pe: 4 } : {})}>
            <Box
              fontSize={item.medal ? 'xl' : undefined}
              textAlign="center"
              w={14}
              {...(item.isCurrentUser
                ? { bgColor: 'brand.500', color: 'white', fontWeight: 'bold', py: 1, rounded: 'full' }
                : { color: 'brand.600' })}
            >
              {item.medal || `${item.rank}${['st', 'nd', 'rd'][item.rank - 1] || 'th'}`}
            </Box>

            <Box flex={1} fontWeight={item.isCurrentUser ? 'bold' : undefined}>
              {item.username || props.t.core.noName()}
            </Box>

            <Box fontSize="sm" fontWeight={item.isCurrentUser ? 'bold' : undefined}>
              {item.points} {props.t.components.rankingStreakCard.points()}
            </Box>
          </HStack>
        ))}
      </Stack>
    )}
  </Card.Root>
);

async function fetchLeaderboardData(
  actorId: string,
  tag: string | undefined,
  since: string,
  until: string
): Promise<LeaderboardEntry[]> {
  const leaderboardJson = await fetchMotiLeaderboard({
    focus: { actorId, take: 3 },
    tag,
    top: { take: 3 },
    type: SOLVE_ACTION_TYPE,
    since,
    until,
  });

  // Anonymize someone inferior to the user
  // const lastRecord = leaderboardJson.focus?.at(-1);
  // if (lastRecord && lastRecord.actorId !== actorId) {
  //   lastRecord.displayName = '';
  // }

  const entries = [...(leaderboardJson.top ?? []), ...(leaderboardJson.focus ?? [])];
  const uniqueEntries = entries.filter(
    (entry, index) => entries.findIndex((e) => e.actorId === entry.actorId) === index
  );
  return uniqueEntries.map((entry) => ({
    rank: entry.rank,
    username: entry.displayName,
    medal: entry.rank <= 3 ? ['🥇', '🥈', '🥉'][entry.rank - 1] : undefined,
    isCurrentUser: entry.actorId === actorId,
    points: entry.aggregateValue,
  }));
}
